import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import useAreAnyAnalyticsQueriesRunning from 'insights/analytics-dashboards/data/useAreAnyAnalyticsQueriesRunning';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {useAnalyticsDashboardContext} from '../AnalyticsDashboardDetailsContext';
import {refreshDashboard} from './RefreshDashboardActions';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

interface Actions {
  refreshDashboard: typeof refreshDashboard;
}

export interface RefreshDashboardButtonProps {
  actions: Actions;
  analyticsDashboard: AnalyticsDashboard;
  className?: string;
}

export function RefreshDashboardButton(props: RefreshDashboardButtonProps): JSX.Element {
  const {analyticsDashboardViewStateId} = useAnalyticsDashboardContext();

  function handleClick(): void {
    props.actions.refreshDashboard(props.analyticsDashboard, analyticsDashboardViewStateId);
  }

  const isLoading = useAreAnyAnalyticsQueriesRunning(props.analyticsDashboard, analyticsDashboardViewStateId);
  return (
    <Tooltip title={isLoading ? 'Refreshing...' : 'Refresh dashboard'}>
      <Button
        onClick={handleClick}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className={props.className}
        disabled={isLoading}
        icon="refresh"
        size={ButtonSize.SMALL}
      />
    </Tooltip>
  );
}

export default function ConnectedRefreshDashboardButton(
  props: Omit<RefreshDashboardButtonProps, 'actions'>
): JSX.Element {
  return <RefreshDashboardButton {...props} actions={{refreshDashboard}} />;
}
